import { editMeetingRoomMinAttendee, TAttendeeLimit, TMeetingRoom } from "apis/meetings/meeting-room";
import {
  CapacityContainer,
  Container,
  HistorySection,
  LabelContainer,
  SettingContainer,
  TitleContainer,
  TopSection,
} from "./MeetingRoomCard.styles";
import UI from "@mile-meeting/mile-ui";
import React, { useRef } from "react";
import { useState } from "react";
import { useToast } from "hook/useToast";
import MinimumAttendeesSettingModal from "../MinimumAttendeesSettingModal/MinimumAttendeesSettingModal";
import { MeetingRoomHorizontalNonModal } from "uis/non-modal";
import AlertModal from "uis/modal/alert-modal";
import { AmplitudeService } from "utils/amplitude.service";
import { useRecoilValue } from "recoil";
import { workspace } from "recoil/recoilState";
import { set } from "lodash";

type MeetingRoomCardProps = {
  meetingRoom: TMeetingRoom;
  onUpdate: () => void;
};

const MeetingRoomCard: React.FC<MeetingRoomCardProps> = ({ meetingRoom, onUpdate }) => {
  const { showToast } = useToast();
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [openSettingModal, setOpenSettingModal] = useState(false);
  const [showDetailInfo, setShowDetailInfo] = React.useState<boolean>(false);
  const [attendeeLimits, setAttendeeLimits] = useState<TAttendeeLimit[]>([]);
  const amplitudeService = new AmplitudeService();
  const workspaceInfo = useRecoilValue(workspace);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const hoverDetailInfo = () => {
    setShowDetailInfo(true);
  };

  const leaveDetailInfo = () => {
    setShowDetailInfo(false);
  };

  //* INFO: 회의실 피크 시간대 최소 참석 인원 설정
  const settingMeetingRoomMinAttendee = async (attendeeLimits: TAttendeeLimit[]) => {
    const res = await editMeetingRoomMinAttendee(meetingRoom?.id ?? "", attendeeLimits);

    if (res.success) {
      setOpenSaveModal(false);
      showToast(`"${meetingRoom?.name ?? ""}" 회의실의 최소 참석 인원 설정이 성공적으로 적용되었습니다.`, "success");

      onUpdate();
    } else {
      showToast(res.message ?? "설정 적용 중 오류가 발생했습니다. 다시 시도해 주세요.", "error");
      setOpenSettingModal(true);
    }
  };

  const getMeetingRoomInfoModalPosition = () => {
    if (titleRef.current && cardRef.current) {
      if (titleRef.current.getClientRects().length === 0) {
        return {
          top: 27,
          left: 0,
        };
      }
      if (titleRef.current?.getClientRects()[0].width > cardRef.current?.getBoundingClientRect().width - 352) {
        return {
          top: 27,
          left: cardRef.current?.getBoundingClientRect().width - 352,
        };
      } else {
        return {
          top: 27,
          left: titleRef.current?.getClientRects()[0].width,
        };
      }
    } else {
      return {
        top: 27,
        left: 0,
      };
    }
  };

  return (
    <Container>
      <TopSection>
        <TitleContainer>
          <div
            ref={cardRef}
            style={{
              gap: 4,
              display: "flex",
              alignItems: "center",
              position: "relative",
              width: "100%",
            }}
          >
            <div
              ref={titleRef}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <UI.Typography type="heading02" fontStyle="bold" color="var(--alias-color-label-normal)">
                {meetingRoom.name}
              </UI.Typography>
              {meetingRoom.meetingRoomIntegration?.integrationProvider === "GOOGLE" && (
                <UI.Icon name="google" size={20} />
              )}
            </div>
            <div onMouseEnter={hoverDetailInfo} onMouseLeave={leaveDetailInfo}>
              <UI.Typography
                type="label01"
                color="var(--alias-color-label-alternative)"
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: "0 4px",
                }}
              >
                상세 정보
              </UI.Typography>
              <MeetingRoomHorizontalNonModal
                meetingRoom={meetingRoom}
                open={showDetailInfo}
                position={getMeetingRoomInfoModalPosition()}
              />
            </div>
          </div>
        </TitleContainer>
        <SettingContainer>
          <CapacityContainer>
            <LabelContainer>
              <UI.Icon name="people" size={16} color="var(--alias-color-label-assistive)" />
              <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                수용 인원
              </UI.Typography>
              <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-neutral)">
                {meetingRoom.capacity}인
              </UI.Typography>
            </LabelContainer>
          </CapacityContainer>
          <UI.Button title="설정" size="small" primary={false} onClick={() => setOpenSettingModal(true)} />
        </SettingContainer>
      </TopSection>
      <HistorySection>
        {meetingRoom.attendeeLimits && meetingRoom.attendeeLimits?.length > 0 ? (
          meetingRoom.attendeeLimits.map((limit: TAttendeeLimit) => (
            <UI.Typography color="var(--alias-color-label-normal)" type="label01" fontStyle="regular">
              · {limit.startTime.substring(0, 5)} - {limit.endTime.substring(0, 5)} | {limit.minAttendeeNum ?? "-"}인
              이상
            </UI.Typography>
          ))
        ) : (
          <UI.Typography color="var(--alias-color-label-alternative)" type="label01" fontStyle="regular">
            설정된 최소 참석 인원 제한이 없습니다.
          </UI.Typography>
        )}
      </HistorySection>
      {/* 최소 참석 인원 설정 모달 */}
      <MinimumAttendeesSettingModal
        modalIsOpen={openSettingModal}
        closeModal={() => setOpenSettingModal(false)}
        room={meetingRoom}
        onUpdate={(attendeeLimits) => {
          setAttendeeLimits(attendeeLimits);
          setOpenSettingModal(false);
          setOpenSaveModal(true);
        }}
      />
      {/* 최소 참석 인원 설정 확인 모달 */}
      <AlertModal
        title={
          <p style={{ wordBreak: "keep-all" }}>{meetingRoom.name} 회의실의 최소 참석 인원 설정을 적용하시겠습니까?</p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-alternative)">
              새롭게 생성되는 예약부터 제한이 적용됩니다.
              <br />
              <UI.Typography color="var(--alias-color-status-negative)">
                * 타 플랫폼으로 정책을 위반한 예약을 생성할 경우,
                <br />
                자동으로 취소 처리됩니다 (반복 예약 제외).
              </UI.Typography>
            </UI.Typography>
          </p>
        }
        btnTitle="적용하기"
        onChange={() => {
          settingMeetingRoomMinAttendee(attendeeLimits);
          amplitudeService.trackingSetMeetingRoomAttendeeLimit(
            workspaceInfo?.id ?? "",
            workspaceInfo?.name ?? "",
            meetingRoom.id,
            meetingRoom.name,
            attendeeLimits
          );
          setOpenSaveModal(false);
        }}
        modalIsOpen={openSaveModal}
        closeModal={() => {
          setOpenSaveModal(false);
          setOpenSettingModal(true);
        }}
      />
    </Container>
  );
};

export default React.memo(MeetingRoomCard);
